<template>
    <!-- One Tab stop for the whole list, moved by the arrows, so Tab reaches the panel's
         footer past hundreds of pieces. -->
    <div dusk="AddOnList" class="flex flex-1 flex-col gap-3" @keydown="moveFocus" @focusin="rememberFocus">
        <p :id="keysHintId" class="sr-only">{{ strings.listKeysHint }}</p>
        <p
            v-if="!visibleAddOns.length"
            dusk="AddOnsEmpty"
            role="status"
            class="px-3 py-6 text-center text-[12.5px] text-theme-toolbar-icon"
        >
            {{ strings.empty }}
        </p>
        <p
            v-else-if="!listedAddOns.length"
            dusk="AddOnsSearchEmpty"
            role="status"
            class="px-3 py-6 text-center text-[12.5px] text-theme-toolbar-icon"
        >
            {{ localizations[lang].searchResultEmpty }}
        </p>
        <div v-for="group in groups" :key="group.key" :dusk="`AddOnGroup-${group.key}`">
            <p
                v-if="group.page !== null"
                dusk="AddOnGroupTitle"
                class="mb-1.5 px-3 text-[10.5px] font-semibold uppercase tracking-wider"
                :class="group.current ? 'text-theme-accent' : 'text-theme-toolbar-icon'"
            >
                {{ group.label }}
                <span class="font-medium">· {{ group.addOns.length }}</span>
                <span v-if="group.current" dusk="AddOnGroupHere" class="font-medium normal-case tracking-normal">
                    · {{ strings.currentPage }}
                </span>
            </p>
            <ul class="m-0 flex list-none flex-col gap-0.5 p-0">
                <li v-for="addOn in group.addOns" :key="addOn.id" class="flex items-center gap-1">
                    <button
                        type="button"
                        :dusk="`AddOnListItem-${addOn.id}`"
                        :data-row-id="addOn.id"
                        data-cell="open"
                        :tabindex="tabindexOf(addOn, 'open')"
                        :aria-describedby="describedByOf(addOn, 'open')"
                        class="group flex min-h-11 min-w-0 flex-1 cursor-pointer items-start gap-2.5 rounded-lg border-none bg-transparent px-3 py-2.5 text-left transition-colors hover:bg-theme-accent-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent md:min-h-0"
                        @click="open(addOn)"
                        @mouseenter="setAddOnHighlight(addOn.id)"
                        @mouseleave="setAddOnHighlight(null)"
                        @focus="setAddOnHighlight(addOn.id)"
                        @blur="setAddOnHighlight(null)"
                    >
                        <svg-icon
                            class="mt-px shrink-0 text-theme-accent"
                            :icon="addOnIcon(addOn.type)"
                            width="20px"
                            height="20px"
                        />
                        <!-- The row's hover tint takes the byline and the draft pill under 4.5:1
                             in some themes, so both take the text colour while it shows. -->
                        <span class="min-w-0 flex-1">
                            <span class="flex items-center gap-1.5">
                                <span class="min-w-0 truncate text-[13px] font-medium text-theme-accent">{{
                                    addOn.title
                                }}</span>
                                <AddOnDraftPill
                                    v-if="isAddOnDraft(addOn)"
                                    class="shrink-0 group-hover:border-theme-text group-hover:text-theme-text"
                                />
                            </span>
                            <span
                                v-if="addOn.author"
                                dusk="AddOnListItemAuthor"
                                class="mt-0.5 block truncate text-[11px] text-theme-toolbar-icon group-hover:text-theme-text"
                            >
                                {{ localizations[lang].by }} {{ addOn.author }}
                            </span>
                        </span>
                    </button>
                    <PillIconButton
                        v-if="canEditAddOns"
                        v-tooltip.top="strings.edit"
                        :dusk="`AddOnListItemEdit-${addOn.id}`"
                        :data-row-id="addOn.id"
                        data-cell="edit"
                        :tabindex="tabindexOf(addOn, 'edit')"
                        :aria-describedby="describedByOf(addOn, 'edit')"
                        icon="baseline-edit-24px"
                        :aria-label="`${strings.edit} · ${addOn.title}`"
                        variant="muted"
                        touch-target
                        class="mr-1 shrink-0"
                        @click="edit(addOn)"
                    />
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import AddOnDraftPill from './AddOnDraftPill.vue';
import PillIconButton from '../Components/PillIconButton.vue';
import { addOnIcon } from './addOnIcon.js';
import { addOnStrings } from './addOnStrings.js';
import { isAddOnDraft } from './addOnModel';

export default {
    name: 'AddOnList',
    components: { AddOnDraftPill, PillIconButton },
    data() {
        return {
            // The button the list's Tab stop sits on: the last one focused.
            active: { id: null, cell: 'open' },
            // Said once, as the list is first reached, until the arrows are in use.
            keysHintHeard: false,
        };
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters(['listedAddOns', 'currentPages', 'visibleAddOns', 'canEditAddOns']),
        strings() {
            return addOnStrings(this.localizations, this.lang);
        },
        keysHintId() {
            return `add-on-list-keys-${this._uid}`;
        },
        rowIds() {
            return this.listedAddOns.map(addOn => String(addOn.id));
        },
        /** A piece the search hid or a pencil a reader no longer has hands the stop back to the first row. */
        tabStop() {
            if (!this.rowIds.includes(this.active.id)) return { id: this.rowIds[0], cell: 'open' };

            return { id: this.active.id, cell: this.canEditAddOns ? this.active.cell : 'open' };
        },
        /**
         * The store already sorts by page (unplaced pieces last) and applies
         * the search, so a group is a run of equal pages. A piece without a
         * page still renders — under no heading — rather than disappear from
         * the list.
         */
        groups() {
            const runs = [];
            for (const addOn of this.listedAddOns) {
                const page = addOn.anchor?.page ?? null;
                const last = runs[runs.length - 1];
                if (last && last.page === page) last.addOns.push(addOn);
                else runs.push({ page, addOns: [addOn] });
            }

            const { page: pageWord } = this.localizations[this.lang];
            return runs.map(run => ({
                ...run,
                key: run.page === null ? 'unplaced' : String(run.page),
                label: run.page === null ? '' : `${pageWord} ${run.page}`,
                current: run.page !== null && this.currentPages.includes(run.page),
            }));
        },
    },
    // A row that unmounts never fires mouseleave, and a marker left open
    // would point at a piece nobody is pointing at.
    beforeDestroy() {
        this.setAddOnHighlight(null);
    },
    methods: {
        ...mapMutations(['setAddOnHighlight']),
        ...mapActions(['goToAddOn', 'editAddOn']),
        addOnIcon,
        isAddOnDraft,
        open(addOn) {
            this.goToAddOn(addOn.id);
        },
        // The form asks before a page turn takes its anchor along, which only
        // holds with the reader on the piece's page, so the piece opens first,
        // the way its row does, and that turns to it.
        edit(addOn) {
            this.goToAddOn(addOn.id);
            this.editAddOn(addOn.id);
        },
        focusFirstRow() {
            this.focusCell(this.rowIds[0], 'open');
        },
        tabindexOf(addOn, cell) {
            return String(addOn.id) === this.tabStop.id && cell === this.tabStop.cell ? 0 : -1;
        },
        describedByOf(addOn, cell) {
            return !this.keysHintHeard && this.tabindexOf(addOn, cell) === 0 ? this.keysHintId : null;
        },
        rememberFocus(event) {
            const { rowId, cell } = event.target.dataset ?? {};
            if (rowId) this.active = { id: rowId, cell };
        },
        focusCell(id, cell) {
            const ids = String(id);
            [...this.$el.querySelectorAll('[data-row-id]')]
                .find(button => button.dataset.rowId === ids && button.dataset.cell === cell)
                ?.focus();
        },
        /** Up and down move between pieces, left and right between a piece and its pencil. */
        moveFocus(event) {
            const { rowId, cell } = event.target.dataset ?? {};
            if (!rowId) return;

            const index = this.rowIds.indexOf(rowId);
            const last = this.rowIds.length - 1;
            const targets = {
                ArrowDown: [this.rowIds[Math.min(index + 1, last)], cell],
                ArrowUp: [this.rowIds[Math.max(index - 1, 0)], cell],
                Home: [this.rowIds[0], cell],
                End: [this.rowIds[last], cell],
                ArrowLeft: [rowId, 'open'],
                ArrowRight: [rowId, this.canEditAddOns ? 'edit' : 'open'],
            };
            const target = targets[event.key];
            if (!target) return;

            // The reader turns the page on a left or right arrow.
            event.preventDefault();
            event.stopPropagation();
            this.keysHintHeard = true;
            this.focusCell(...target);
        },
    },
};
</script>
